import { createApolloClient } from "../../services/apolloClient";
import {
  AuthPayload,
  LoginInput,
  LoginResponse,
  RegisterInput,
  RegisterResponse,
  VerifyOtpInput,
  VerifyOtpResponse,
  VerifyPhoneInput,
  VerifyPhoneResponse,
  VerifyStatusResponse,
} from "./types";
import {
  CUSTOMER_REGISTER_MUTATION,
  LOGIN_MUTATION,
  VERIFY_OTP_MUTATION,
  VERIFY_PHONE_MUTATION,
} from "./mutations";

const getErrorMessage = (error: unknown, fallback: string) => {
  if (!error || typeof error !== "object") {
    return fallback;
  }

  const graphQLErrors = (
    error as { graphQLErrors?: Array<{ message?: string }> }
  ).graphQLErrors;

  if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
    const firstMessage = graphQLErrors[0]?.message;
    if (firstMessage) {
      return firstMessage.replace(/^Error:\s*/i, "");
    }
  }

  if (error instanceof Error && error.message) {
    return error.message.replace(/^Error:\s*/i, "");
  }

  return fallback;
};

const client = createApolloClient();

export const authService = {
  async customerRegister(input: RegisterInput): Promise<AuthPayload> {
    try {
      const result = await client.mutate({
        mutation: CUSTOMER_REGISTER_MUTATION,
        variables: input,
      });

      const data = result.data as RegisterResponse | undefined;

      if (!data?.customerRegister) {
        throw new Error("Register failed");
      }

      return data.customerRegister;
    } catch (error) {
      throw new Error(getErrorMessage(error, "Register failed"));
    }
  },

  async login(input: LoginInput): Promise<AuthPayload> {
    try {
      const result = await client.mutate({
        mutation: LOGIN_MUTATION,
        variables: input,
      });

      const data = result.data as LoginResponse | undefined;

      if (!data?.login) {
        throw new Error("Login failed");
      }

      return data.login;
    } catch (error) {
      throw new Error(getErrorMessage(error, "Login failed"));
    }
  },

  async verifyPhone(input: VerifyPhoneInput): Promise<VerifyStatusResponse> {
    try {
      const result = await client.mutate({
        mutation: VERIFY_PHONE_MUTATION,
        variables: input,
      });

      const data = result.data as VerifyPhoneResponse | undefined;

      if (!data?.verifyPhone) {
        throw new Error("Verify phone failed");
      }

      return data.verifyPhone;
    } catch (error) {
      throw new Error(getErrorMessage(error, "Verify phone failed"));
    }
  },

  async verifyOtp(input: VerifyOtpInput) {
    try {
      const result = await client.mutate({
        mutation: VERIFY_OTP_MUTATION,
        variables: input,
      });

      const data = result.data as VerifyOtpResponse | undefined;

      if (!data?.verifyOtp) {
        throw new Error("Verify OTP failed");
      }

      return data.verifyOtp;
    } catch (error) {
      throw new Error(getErrorMessage(error, "Verify OTP failed"));
    }
  },
};
